import {
  Box,
  Checkbox,
  CheckboxProps,
  Label,
  Tag,
} from '@nimbus-ds/components';
import { GetDocumentSettingsResponseDto } from '@tiendanube/common';
import { Stack } from 'commons/components';
import { useHasUnifiedShippingDocumentsEnabled } from 'domains/Auth/hooks';
import useTranslationFulfillmentOrders from 'domains/FulfillmentOrders/useTranslationFulfillmentOrders';
import { DocumentType } from '../../constants';
import LABEL_SETTINGS_OPTIONS, {
  LABEL_SETTING_RECEIPT_STUP,
  LABEL_SETTING_SUB_OPTIONS,
  SHOW_PRODUCTS_INFO_LABEL,
} from '../../LabelSettings';

interface PrintDocumentConfigurationProps {
  settings: GetDocumentSettingsResponseDto;
  isLoading: boolean;
  onChangeSetting: (name: string, value: boolean) => void;
  hasPickup: boolean;
  type: DocumentType;
  hasOca: boolean;
}

function DocumentSettingsForm({
  settings,
  isLoading,
  onChangeSetting,
  hasPickup,
  type,
  hasOca,
}: Readonly<PrintDocumentConfigurationProps>) {
  const t = useTranslationFulfillmentOrders('printManager.informationCard');
  const isUnifiedShippingDocumentsEnabled =
    useHasUnifiedShippingDocumentsEnabled();
  const isShippingDocument = type === DocumentType.SHIPPING_DOCUMENT;
  const isOcaWithUnified =
    type === DocumentType.LABEL && hasOca && isUnifiedShippingDocumentsEnabled;

  const currentSettings: CheckboxProps[] = LABEL_SETTINGS_OPTIONS.map(
    ({ label, name }) => ({
      label,
      name,
      checked: settings[name],
    }),
  );

  const shouldShowEnhancedOptions = isShippingDocument || isOcaWithUnified;
  const baseSubOptions = shouldShowEnhancedOptions
    ? [
        { label: 'showCheckboxPicking', name: 'showCheckboxPicking' },
        { label: 'showProductImage', name: 'showProductImage' },
        ...LABEL_SETTING_SUB_OPTIONS,
      ]
    : LABEL_SETTING_SUB_OPTIONS;

  const subCurrentSettings: CheckboxProps[] = baseSubOptions.map(
    ({ label, name }) => ({
      label,
      name,
      checked: settings[name] && settings[SHOW_PRODUCTS_INFO_LABEL],
      disabled: !settings[SHOW_PRODUCTS_INFO_LABEL],
    }),
  );

  const allSubUnchecked = subCurrentSettings.every(({ checked }) => !checked);
  const activeSubCheckboxes = subCurrentSettings.filter(
    ({ checked }) => checked,
  ).length;

  const subCheckBoxList = Object.keys(subCurrentSettings).map((key) => {
    const { name, checked, label } = subCurrentSettings[key];
    const isNewOption =
      shouldShowEnhancedOptions &&
      (name === 'showCheckboxPicking' || name === 'showProductImage');

    return (
      <Box key={'subbox-' + key} marginLeft="5">
        <Stack align="center" justify="space-between">
          <Checkbox
            key={key}
            name={name}
            checked={checked}
            disabled={!settings[SHOW_PRODUCTS_INFO_LABEL]}
            label={label ? t(label) : ''}
            onChange={(event) => onChangeSetting(name, event.target.checked)}
          />
          {isNewOption && <Tag appearance="primary">{t('newTag')}</Tag>}
        </Stack>
      </Box>
    );
  });

  const baseCheckBoxList = Object.keys(currentSettings).map((key) => {
    const { name, checked, label } = currentSettings[key];
    const isIndeterminate =
      checked &&
      (activeSubCheckboxes < subCurrentSettings.length || allSubUnchecked);

    return (
      <Box key={'box-' + key}>
        {label === SHOW_PRODUCTS_INFO_LABEL && (
          <Stack justify="flex-start" spacing="base" align="stretch" column>
            <Checkbox
              key={key}
              name={name}
              checked={
                checked && activeSubCheckboxes >= subCurrentSettings.length
              }
              indeterminate={isIndeterminate}
              label={label ? t(label) : ''}
              onChange={(event) => {
                if (event.target.checked && isIndeterminate) {
                  onChangeSetting(name, !event.target.checked);
                  return;
                }
                onChangeSetting(name, event.target.checked);
              }}
            />
            {subCheckBoxList}
          </Stack>
        )}

        {label !== SHOW_PRODUCTS_INFO_LABEL && (
          <Checkbox
            key={key}
            name={name}
            checked={checked}
            label={label ? t(label) : ''}
            onChange={(event) => onChangeSetting(name, event.target.checked)}
          />
        )}
      </Box>
    );
  });

  return (
    <Stack justify="flex-start" align="stretch" column>
      <Label>{t('title')}</Label>
      {!isLoading && baseCheckBoxList}
      {!isLoading && hasPickup && (
        <Stack justify="space-between">
          <Checkbox
            key={LABEL_SETTING_RECEIPT_STUP.name}
            name={LABEL_SETTING_RECEIPT_STUP.name}
            checked={settings[LABEL_SETTING_RECEIPT_STUP.name]}
            label={t(LABEL_SETTING_RECEIPT_STUP.label ?? '')}
            onChange={(event) =>
              onChangeSetting(
                LABEL_SETTING_RECEIPT_STUP.name,
                event.target.checked,
              )
            }
          />
        </Stack>
      )}
      {isLoading && (
        <>
          <Checkbox.Skeleton />
          <Checkbox.Skeleton />
          <Checkbox.Skeleton />
          <Checkbox.Skeleton />
          <Checkbox.Skeleton />
          <Checkbox.Skeleton />
          <Checkbox.Skeleton />
        </>
      )}
    </Stack>
  );
}

export default DocumentSettingsForm;
